/**
 * ChangePasswordPage — signed-in members change their existing password.
 * Uses the pre-existing /api/auth/member/change-password endpoint.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, formatApiError } from "../../lib/authApi";
import { AuthShell, Field, PrimaryBtn, Notice, SmallLink } from "../../components/auth/AuthShell";

const ChangePasswordPage = () => {
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [newpw, setNewpw] = useState("");
  const [newpw2, setNewpw2] = useState("");
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (newpw !== newpw2) { setErr("New passwords do not match."); return; }
    if (newpw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await authApi.changePassword({
        current_password: current, new_password: newpw, new_password_confirm: newpw2,
      });
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Password change failed.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <AuthShell title="Password updated" lead="Your password was changed and all other sessions were signed out.">
        <Notice kind="success" data-testid="change-pw-success">
          Password updated. This session remains active.
        </Notice>
        <PrimaryBtn onClick={() => nav("/members/security")} type="button" data-testid="change-pw-back">
          BACK TO SECURITY
        </PrimaryBtn>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Change password"
      lead="Provide your current password and choose a new one. Other active sessions will be signed out — this one stays."
      footer={<SmallLink to="/members/security" data-testid="change-pw-cancel">CANCEL</SmallLink>}
    >
      {err && <Notice kind="error" data-testid="change-pw-error">{err}</Notice>}
      <form onSubmit={onSubmit} data-testid="change-pw-form">
        <Field label="CURRENT PASSWORD" type="password" value={current} onChange={setCurrent}
               required autoComplete="current-password" autoFocus
               data-testid="change-pw-current" name="current_password" />
        <Field label="NEW PASSWORD" type="password" value={newpw} onChange={setNewpw}
               required autoComplete="new-password"
               data-testid="change-pw-new" name="new_password" />
        <Field label="CONFIRM NEW PASSWORD" type="password" value={newpw2} onChange={setNewpw2}
               required autoComplete="new-password"
               data-testid="change-pw-new-confirm" name="new_password_confirm" />
        <PrimaryBtn disabled={busy} data-testid="change-pw-submit">
          {busy ? "PROCESSING…" : "UPDATE PASSWORD"}
        </PrimaryBtn>
      </form>
    </AuthShell>
  );
};

export default ChangePasswordPage;
